import {FC, useState, useLayoutEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {Form, Formik} from 'formik';
import cx from 'classnames';

// MUI
import {IconButton} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';

// Core
import {ROUTERS} from 'core/_consts';
import {lastSegmentOfPath} from 'core/utils';
import {NewProject} from 'core/_types/NewProject';

// Components
import StepSlider from 'shared/components/StepSlider';
import Header from 'shared/components/Header';
import {THEMES} from 'shared/helpers';

import ProjectView from 'pages/ProjectView';
import LenderView from 'pages/LenderView';
import {ProjectViewTabs} from 'pages/ProjectView/helpers';

import {
  StartPage,
  First,
  Second,
  Third,
  Fourth,
  Fifth,
  Sixth,
  Seventh,
  Eighth,
  Ninth,
  Tenth,
  Eleventh,
  Twelfth,
  Thirteenth,
} from '../Steps';

import {NewOfferSliderSteps} from '../helpers';

import s from './CreateNewOfferWrapper.module.scss';

const useStyles = makeStyles({
  button: {
    position: 'absolute',
    padding: 0,
    color: 'white',
    font: '600 18px Source Sans Pro',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '@media(max-width: 768px)': {
      display: '',
    },
  },
  close: {
    right: 20,
    top: 20,
    zIndex: 1,
    '@media(max-width: 425px)': {
      top: 14,
      right: 14,
    },
  },
  back: {
    left: 20,
    bottom: 20,
    gap: '5px',
  },
  next: {
    right: 20,
    bottom: 20,
    gap: '5px',
  },
});

const stepsTitles = Array(14)
  .fill('Step')
  .map((item, index) => `${item} ${index + 1}`);

type StepKeysType = keyof typeof NewOfferSliderSteps;

const CreateNewOfferWrapper: FC = () => {
  const [isStartPoint, setIsStartPoint] = useState(false);
  const [step, setStep] = useState<StepKeysType>(
    NewOfferSliderSteps[NewOfferSliderSteps['new-user-email']] as StepKeysType
  );

  const history = useHistory();
  const location = useLocation();

  const classes = useStyles();

  const renderStep = () => {
    switch (step) {
      case NewOfferSliderSteps[NewOfferSliderSteps['new-user-email']]:
        return <StartPage />;

      case NewOfferSliderSteps[NewOfferSliderSteps['asset-group']]:
        return <First goNext={goNext} />;

      case NewOfferSliderSteps[NewOfferSliderSteps['asset-type']]:
        return <Second goNext={goNext} />;

      case NewOfferSliderSteps[NewOfferSliderSteps['financing-purpose']]:
        return <Third goNext={goNext} />;

      case NewOfferSliderSteps[NewOfferSliderSteps.address]:
        return <Fourth />;

      case NewOfferSliderSteps[NewOfferSliderSteps['property-income']]:
        return <Fifth />;

      case NewOfferSliderSteps[NewOfferSliderSteps['project-budget']]:
        return <Sixth />;

      case NewOfferSliderSteps[NewOfferSliderSteps['financing-request']]:
        return <Seventh />;

      case NewOfferSliderSteps[NewOfferSliderSteps['financing-details']]:
        return <Eighth />;

      case NewOfferSliderSteps[NewOfferSliderSteps.attachments]:
        return <Ninth />;

      case NewOfferSliderSteps[NewOfferSliderSteps['sponsor-role']]:
        return <Eleventh goNext={goNext} />;

      case NewOfferSliderSteps[NewOfferSliderSteps.guarantors]:
        return <Twelfth goNext={goNext} />;

      case NewOfferSliderSteps[NewOfferSliderSteps['project-summary']]:
        return <Tenth />;

      case NewOfferSliderSteps[NewOfferSliderSteps.finalize]:
        return <Thirteenth />;

      default:
        return null;
    }
  };

  const initialValues: NewProject = {
    firstName: '',
    lastName: '',
    email: '',
    experienceLevel: '',
    targetCloseDate: '',
    line1: '',
    city: '',
    state: '',
    zip: '',
    assetType: {
      value: '',
      isPicked: false,
    },
    investmentProperty: {
      value: '',
      isPicked: false,
    },
    tenantsType: {
      value: '',
      isPicked: false,
    },
    tenants: '',
    hotelFlag: '',
    hotelExpirationDate: '',
    financingPurpose: '',
    sponsorRole: '',
    multiFamilyAcquisition: '',
    loanTerm: '',
    amortization: '',
    recourse: '',
    interestRate: '',
    notes: '',
  };

  const data = localStorage.getItem('projectInitialValues');
  const projectInitialValues = data && JSON.parse(data);

  const goNext = () => {
    const currentStep = NewOfferSliderSteps[
      NewOfferSliderSteps[step as keyof typeof NewOfferSliderSteps] + 1
    ] as StepKeysType;

    setStep(currentStep);

    history.replace(`${ROUTERS.CREATE_NEW_PROJECT}/${currentStep}`);
  };

  const nextStep = (values: any) => {
    const currentStep = NewOfferSliderSteps[
      NewOfferSliderSteps[step as keyof typeof NewOfferSliderSteps] + 1
    ] as StepKeysType;
    setStep(currentStep);

    localStorage.setItem('projectInitialValues', JSON.stringify(values));

    history.replace(`${ROUTERS.CREATE_NEW_PROJECT}/${currentStep}`);
  };

  const prevStep = () => {
    const currentStep = NewOfferSliderSteps[
      NewOfferSliderSteps[step as keyof typeof NewOfferSliderSteps] - 1
    ] as StepKeysType;

    if (!isStartPoint) {
      setStep(currentStep);
      history.replace(`${ROUTERS.CREATE_NEW_PROJECT}/${currentStep}`);
    }
  };

  const prevContent = (values: any, setFieldValue: any) => {
    if (values.tenantsType.isPicked) {
      setFieldValue('tenantsType.isPicked', false);
    }
    if (values.investmentProperty.isPicked) {
      setFieldValue('investmentProperty.isPicked', false);
      setFieldValue('investmentProperty.value', '');
    }
    setFieldValue('assetType.isPicked', false);
  };

  useLayoutEffect(() => {
    const currentStep =
      (lastSegmentOfPath(location.pathname) as StepKeysType) ||
      NewOfferSliderSteps[NewOfferSliderSteps['new-user-email']];
    setStep(currentStep);
    setIsStartPoint(currentStep === NewOfferSliderSteps[NewOfferSliderSteps['new-user-email']]);
  }, [step]);

  if (
    location.pathname.includes(ProjectViewTabs[ProjectViewTabs.underwriting]) ||
    location.pathname.includes(ProjectViewTabs[ProjectViewTabs['capital-sources']]) ||
    location.pathname.includes(ProjectViewTabs[ProjectViewTabs.offers])
  )
    return <ProjectView />;

  if (location.pathname.includes('view')) return <LenderView />;

  return (
    <>
      <Header />
      <Formik
        enableReinitialize
        initialValues={projectInitialValues || initialValues}
        // eslint-disable-next-line no-console
        onSubmit={(values) => console.log('Values: ', values)}
      >
        {({values, setFieldValue}) => (
          <Form>
            <div className={s.createNewOfferWrapper}>
              <IconButton
                className={cx(classes.button, classes.close)}
                disableRipple
                onClick={() => history.push(ROUTERS.LOGIN)}
                size="small"
              >
                <CloseIcon />
              </IconButton>

              <div className={s.createNewOfferWrapper__content}>{renderStep()}</div>
              <div className={s.createNewOfferWrapper__sliderWrapper}>
                {step && (
                  <StepSlider
                    titles={stepsTitles}
                    theme={THEMES.white}
                    steps={Object.values(NewOfferSliderSteps)}
                    currentStep={NewOfferSliderSteps[step as keyof typeof NewOfferSliderSteps]}
                  />
                )}
              </div>

              {!isStartPoint && (
                <IconButton
                  className={cx(classes.button, classes.back, s.createNewOfferWrapper__backBtn)}
                  disableRipple
                  onClick={step === 'asset-group' ? () => prevContent(values, setFieldValue) : prevStep}
                  size="small"
                >
                  <ArrowBackIcon />
                  <span>Back</span>
                </IconButton>
              )}
              <IconButton
                className={cx(classes.button, classes.next, s.createNewOfferWrapper__nextBtn)}
                disableRipple
                onClick={() => nextStep(values)}
                size="small"
              >
                <span>Next</span>
                <ArrowForwardIcon />
              </IconButton>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateNewOfferWrapper;
